import { LitElement, html, unsafeCSS } from "lit-element"
import { classMap } from "lit-html/directives/class-map"
import DateCell from "./date-cell"
import commonStyle from "../common.css"
import style from "./calendar.css"
import Constant from "./constant.js"

const plusMonth = (date, deltaMonth) => {
	const lastDayOfTargetMonth = new Date(date.getFullYear(), date.getMonth() + deltaMonth + 1, 0)
	if (lastDayOfTargetMonth.getDate() < date.getDate()) {
		date.setMonth(date.getMonth() + deltaMonth, lastDayOfTargetMonth.getDate())
	} else {
		date.setMonth(date.getMonth() + deltaMonth)
	}
}

const moveMonth = (date, monthValue) => {
	const lastDayOfTargetMonth = new Date(date.getFullYear(), monthValue + 1, 0)
	if (lastDayOfTargetMonth.getDate() < date.getDate()) {
		date.setMonth(monthValue, lastDayOfTargetMonth.getDate())
	} else {
		date.setMonth(monthValue)
	}
}

const moveYear = (date, year) => {
	const lastDayOfTargetMonth = new Date(year, date.getMonth() + 1, 0)
	if (lastDayOfTargetMonth.getDate() < date.getDate()) {
		date.setFullYear(year, date.getMonth(), lastDayOfTargetMonth.getDate())
	} else {
		date.setFullYear(year)
	}
}

/**
 * カレンダーコンポーネント
 */
export default class Calendar extends LitElement {
	static styles = [unsafeCSS(style), unsafeCSS(commonStyle)]
	static get properties () {
		return {
			mode: { type: String },
			secondary: { type: Boolean },
			emphasisWeekend: { type: Boolean, attribute: "emphasis-weekend" },
			targetYearMonth: { type: String, attribute: "target-year-month" },
			fix: { type: Boolean },
			holiday: { type: Boolean }
		}
	}

	/**
	 * ダイアログ取得
	 */
	get #dialogEl () {
		return this.shadowRoot.querySelector("bui-dialog")
	}

	/**
	 * ポップアップ取得
	 */
	get #popupEl () {
		return this.shadowRoot.querySelector("bui-popup")
	}

	/**
	 * @type {boolean}
	 */
	isOpen = false

	/**
	 * @type {boolean}
	 */
	isDialog = false

	/**
	 * @type {Date}
	 */
	showDate = new Date()

	/**
	 * @type {Date}
	 */
	currDate = new Date()

	/**
	 * @type {Date[]}
	 */
	holidays = []

	/**
	 * @type {Number[]}
	 */
	years = []

	/**
	 * @type {string}
	 */
	page = null

	constructor () {
		super()
		/** 表示形式 */
		this.mode = Constant.MODE.Date
		/** セカンダリ色 */
		this.secondary = false
		/** 曜日色付け */
		this.emphasisWeekend = false
		/** 初期表示年月 */
		this.targetYearMonth = ""
		/** 年月固定フラグ */
		this.fix = false
		/** 祝日 */
		this.holiday = false
	}

	render () {
		if (!this.isOpen) return html``

		// カレンダーを生成
		const calendar = html`
			${this.createTitle()}
			${this.createCalendarBody()}
		`
		// 生成したカレンダーをダイアログまたはポップアップに貼り付け
		return html`
			${this.isDialog ? html`
				<!-- ダイアログカレンダー -->
				<bui-dialog full-screen focuse-on-open no-close-icon no-header>
					<div class=${this.#calendarClass()}>
						${calendar}
					</div>
				</bui-dialog>
			` : html`
				<!-- ポップアップカレンダー -->
				<bui-popup static>
					<div class=${this.#calendarClass(true)}>
						${calendar}
					</div>
				</bui-popup>
			`}
		`
	}

	#calendarClass (isPopup = false) {
		return classMap({
			calendar: true,
			shadow: true,
			secondary: this.secondary,
			"calendar-popup": isPopup
		})
	}

	/***************************************************************/
	/* メイン */
	/***************************************************************/
	/**
	 * タイトルエリアの生成.
	 */
	createTitle () {
		const year = this.mode !== Constant.MODE.Time
		const month = this.mode === Constant.MODE.Month || this.mode === Constant.MODE.Date || this.mode === Constant.MODE.DateTime
		const date = this.mode === Constant.MODE.Date || this.mode === Constant.MODE.DateTime
		const time = this.mode === Constant.MODE.Time || this.mode === Constant.MODE.DateTime

		// 現在選択されている日時を表示する
		return html`
			<div class="title">
				${!this.isDialog ? html`` : html`
					<bui-icon large class="icon icon-button hl" icon="fas fa-times" tabindex="0" @click="${this.close}"></bui-icon><br>
				`}
				${!year ? html`` : html`
					<button is="bui-button" class="bui-button is-text" @click=${this.selectYearCalendar}>
						${this.currDate.getFullYear()}年
					</div>
				`}
				${!month ? html`` : html`
					<button is="bui-button" class="bui-button is-text" @click=${this.selectMonthCalendar}>
						${this.currDate.getMonth() + 1}月
					</div>
				`}
				${!date ? html`` : html`
					<button is="bui-button" class="bui-button is-text" @click=${this.selectDateCalendar}>
						${this.currDate.getDate()}日（${Constant.WEEK_DAYS[this.currDate.getDay()]}）
					</div>
				`}
				${!time ? html`` : html`
					<button is="bui-button" class="bui-button is-text" @click=${this.selectTimeCalendar}>
						${("0" + this.currDate.getHours()).slice(-2)}：${("0" + this.currDate.getMinutes()).slice(-2)}
					</div>
				`}
			</div>
		`
	}

	/**
	 * カレンダーボディ部生成.
	 */
	createCalendarBody () {
		return html`
			<div class="body">
				${this.page === Constant.MODE.Year ? this.createYearCalendar() : ""}
				${this.page === Constant.MODE.Month ? this.createMonthCalendar() : ""}
				${this.page === Constant.MODE.Date ? this.createDateCalendar() : ""}
				${this.page === Constant.MODE.Time ? this.createTimeCalendar() : ""}
			</div>
		`
	}

	/**
	 * ダイアログでカレンダーを表示する
	 * @param {Object} arg
	 * @param {Object} arg.date
	 */
	showAsDialog (arg) {
		this.isOpen = true
		this.isDialog = true
		this.initShow(arg.date)

		this.requestUpdate().then(() => {
			// ダイアログを開く
			this.#dialogEl.open()
			// 選択されている年/月/時間をリストエリアの中央に配置する
			this.centerCurrList()
		})

		return new Promise(resolve => {
			this.resolve = resolve
		})
	}

	/**
	 * ポップアップでカレンダーを表示する
	 * @param {Object} arg
	 * @param {HTMLElement} arg.target
	 * @param {string} arg.position
	 * @param {boolean} arg.offsetY
	 * @param {boolean} arg.offsetX
	 * @param {Date} arg.date
	 */
	showAsPopup (arg) {
		this.isOpen = true
		this.isDialog = false
		if (!arg.position) arg.position = "bottom-right"
		if (arg.offsetY == null) arg.offsetY = true
		this.initShow(arg.date)

		this.requestUpdate().then(() => {
			// ポップアップを開く
			this.#popupEl.showAtTarget(arg)
			// 選択されている年・月・時間をリストエリアの中央に配置する
			this.centerCurrList()
		})

		return new Promise(resolve => {
			this.resolve = resolve
		})
	}

	/**
	 * カレンダー初期表示時の設定
	 * @param {Date} date
	 */
	initShow (date) {
		// 現在日を取得
		if (date) {
			// カレンダー表示メソッドの引数に指定がある場合
			this.currDate = date
		} else if (this.targetYearMonth) {
			// 初期表示年月プロパティに指定がある場合
			this.currDate = new Date(this.targetYearMonth)
		} else {
			// 上記以外
			this.currDate = new Date()
		}

		// 表示日を取得
		this.showDate = new Date(this.currDate)

		// 表示ページ・表示形式を設定
		switch (this.mode) {
		case Constant.MODE.Year:
			this.page = Constant.MODE.Year
			break
		case Constant.MODE.Month:
			this.page = Constant.MODE.Month
			break
		case Constant.MODE.Time:
			this.page = Constant.MODE.Time
			break
		case Constant.MODE.DateTime:
			this.page = Constant.MODE.Date
			break
		default:
			this.page = Constant.MODE.Date
			this.mode = Constant.MODE.Date
		}

		// 年月固定フラグを設定
		if (this.mode !== Constant.MODE.Date && this.mode !== Constant.MODE.DateTime) {
			// 日カレンダー・日時カレンダーでない場合は年月固定フラグを無効にする
			this.fix = false
		}
	}

	/**
	 * 日付を祝日リストに追加する
	 * @param {Date} date
	 */
	addHoliday (date) {
		this.holidays.push(date.getTime())
	}

	/**
	 * 日にち決定イベント
	 */
	settleDate () {
		this.isOpen = false
		if (this.isDialog) this.#dialogEl.close()
		else this.#popupEl.hide()
		// イベント登録
		this.dispatchEvent(new CustomEvent("settle-date", { detail: { date: this.currDate } }))

		if (this.resolve) {
			this.resolve(this.currDate)
			this.resolve = null
		}
	}

	/**
	 * クローズイベント
	 */
	close () {
		this.isOpen = false
		if (this.isDialog) this.#dialogEl.close()
		else this.#popupEl.hide()
		// イベント登録
		this.dispatchEvent(new CustomEvent("close"))
		if (this.resolve) {
			this.resolve()
			this.resolve = null
		}
	}

	/**
	 * 年カレンダー選択イベント
	 */
	selectYearCalendar () {
		if (!this.fix) {
			this.page = Constant.MODE.Year
		}
		this.requestUpdate().then(() => {
			// 選択されている年を中央に配置する
			this.centerCurrYear()
		})
	}

	/**
	 * 月カレンダー選択イベント
	 */
	selectMonthCalendar () {
		if (!this.fix) {
			this.page = Constant.MODE.Month
		}
		this.requestUpdate().then(() => {
			// 選択されている月を中央に配置する
			this.centerCurrMonth()
		})
	}

	/**
	 * 日カレンダー選択イベント
	 */
	selectDateCalendar () {
		this.page = Constant.MODE.Date
		this.requestUpdate()
	}

	/**
	 * 時間カレンダー選択イベント
	 */
	selectTimeCalendar () {
		this.page = Constant.MODE.Time
		this.requestUpdate().then(() => {
			// 選択されている時間を中央に配置する
			this.centerCurrTime()
		})
	}

	/**
	 * 選択されている年・月・時間をリストエリアの中央に配置する
	 */
	centerCurrList () {
		if (this.page === Constant.MODE.Year) {
			this.centerCurrYear()
		} else if (this.page === Constant.MODE.Month) {
			this.centerCurrMonth()
		} else if (this.page === Constant.MODE.Time) {
			this.centerCurrTime()
		}
	}

	/**
	 * 選択されている年をリストエリアの中央に配置する
	 */
	centerCurrYear () {
		setTimeout(() => {
			const yearCalendar = this.shadowRoot.querySelector(".year")
			const currentList = yearCalendar.querySelector(".selected")
			yearCalendar.scrollTop = currentList.offsetTop + currentList.getBoundingClientRect().height / 2 - yearCalendar.getBoundingClientRect().height / 2
		}, 200)
	}

	/**
	 * 選択されている月をリストエリアの中央に配置する
	 */
	centerCurrMonth () {
		setTimeout(() => {
			const monthCalendar = this.shadowRoot.querySelector(".month")
			const currentList = monthCalendar.querySelector(".selected")
			monthCalendar.scrollTop = currentList.offsetTop - monthCalendar.getBoundingClientRect().height / 2
		}, 200)
	}

	/**
	 * 選択されている時間をリストエリアの中央に配置する
	 */
	centerCurrTime () {
		setTimeout(() => {
			// 時
			const hourListArea = this.shadowRoot.querySelector(".time .hour")
			const currentHourList = hourListArea.querySelector(".selected")
			hourListArea.scrollTop = currentHourList.offsetTop - hourListArea.getBoundingClientRect().height / 2
			// 分
			const minuteListArea = this.shadowRoot.querySelector(".time .minute")
			const currentMinuteList = minuteListArea.querySelector(".time .minute .selected")
			minuteListArea.scrollTop = currentMinuteList.offsetTop - minuteListArea.getBoundingClientRect().height / 2
		}, 200)
	}

	/***************************************************************/
	/* 年カレンダー */
	/***************************************************************/
	/**
	 * 年カレンダー生成
	 */
	createYearCalendar () {
		if (this.years.length === 0) {
			// 初期表示時は現在年の前後15年分を追加
			const currYear = this.showDate.getFullYear()
			for (let i = 0; i < 15; i++) { this.years.push(currYear + i) }
			for (let i = 1; i < 15; i++) { this.years.unshift(currYear - i) }
		}
		return html`
			<div class="year" style=${this.page === Constant.MODE.Year ? "" : "display:none"} @mousewheel=${this.scrollYear}>
				${this.years.map(year => html`
					<li value=${year} class=${year === this.showDate.getFullYear() ? "selected hl" : "hl"} @click=${this.selectYear}>
						${year}年
					</li>
				`)}
			</div>
		`
	}

	/**
	 * 年カレンダースクロールイベント
	 */
	scrollYear (e) {
		if (e.deltaY < 0) {
			this.years.unshift(this.years[0] - 1)
		} else if (e.deltaY > 0) {
			this.years.push(this.years[this.years.length - 1] + 1)
		}
		this.requestUpdate()
	}

	/**
	 * 年選択イベント
	 */
	selectYear (e) {
		moveYear(this.showDate, Number(e.currentTarget.value))
		moveYear(this.currDate, Number(e.currentTarget.value))
		// this.showDate.setYear(e.currentTarget.value)
		// this.currDate.setYear(e.currentTarget.value)
		if (this.mode !== Constant.MODE.Year) {
			this.page = Constant.MODE.Month
			this.requestUpdate().then(() => {
				// 選択されている月を中央に配置する
				this.centerCurrMonth()
			})
		} else {
			this.settleDate()
		}
	}

	/***************************************************************/
	/* 月カレンダー */
	/***************************************************************/
	/**
	 * 月カレンダー生成
	 */
	createMonthCalendar () {
		const monthList = [...new Array(12).keys()]
		return html`
			<div class="month" style=${this.page === Constant.MODE.Month ? "" : "display:none"}>
				${monthList.map(month => html`
					<li value=${month} class=${month === this.showDate.getMonth() ? "selected hl" : " hl"} @click=${this.selectMonth}>
						${month + 1}月
					</li>
				`)}
			</div>
		`
	}

	/**
	 * 月選択イベント
	 */
	selectMonth (e) {
		moveMonth(this.showDate, Number(e.currentTarget.value))
		moveMonth(this.currDate, Number(e.currentTarget.value))
		if (this.mode !== Constant.MODE.Month) {
			this.page = Constant.MODE.Date
			this.requestUpdate()
		} else {
			this.settleDate()
		}
	}

	/***************************************************************/
	/* 日カレンダー */
	/***************************************************************/
	/**
	 * 日カレンダー生成
	 */
	createDateCalendar () {
		// 曜日列を作成
		const weeksHtml = html`
			<tr>
				${Constant.WEEK_DAYS.map((day, dayIndex) => html`
					<td>
						<div class=${this.titleCellClass(dayIndex)}>
							<div>${day}</div>
						</div>
					</td>
				`)}
			</tr>
		`

		// 今月の日にちを配列に追加
		const currYear = this.showDate.getFullYear()
		const currMonth = this.showDate.getMonth()
		const currMonthLength = new Date(currYear, currMonth + 1, 0).getDate()
		const currMonthDates = [...new Array(currMonthLength).keys()].map(date => new Date(currYear, currMonth, date + 1))

		// 前月の日にちを追加
		const prevMonthLength = new Date(currYear, currMonth, 1).getDay()
		for (let i = 0; i < prevMonthLength; i++) {
			currMonthDates.unshift(new Date(currYear, currMonth, 0 - i))
		}

		// 翌月の日にちを追加
		const nextMonthLength = 42 - currMonthDates.length
		for (let i = 0; i < nextMonthLength; i++) {
			currMonthDates.push(new Date(currYear, currMonth + 1, i + 1))
		}

		const properties = {
			mode: this.mode,
			secondary: this.secondary,
			emphasisWeekend: this.emphasisWeekend,
			targetYearMonth: this.targetYearMonth,
			fix: this.fix,
			holiday: this.holiday
		}
		// DateCellクラスで保持
		let dateCells = currMonthDates.map(date => new DateCell(date, currMonth, this.currDate, this.showDate, this.holidays, this, properties))

		// 週ごとに分割して保持する
		const weeks = []
		while (dateCells.length !== 0) {
			const temp = dateCells.slice(0, 7)
			weeks.push(temp)
			dateCells = dateCells.splice(7)
		}

		// 日にち列を作成
		const datesHtml = html`
			${weeks.map(dates => html`
				<tr>
					${dates.map(cell => cell.render())}
				</tr>
			`)}
		`

		// html生成して返却する
		return html`
			<div class="date" style=${this.page === Constant.MODE.Date ? "" : "display:none"}>
				<!-- ヘッダー -->
				${this.createDateCalendarHeader()}
				<!-- テーブル -->
				<div class="dateCalendarBody">
					<table>
						${weeksHtml}
						${datesHtml}
					</table>
				</div>
				<!-- フッター -->
				${this.createDateCalendarFooter()}
			</div>
		`
	}

	/**
	 * 日カレンダーヘッダー部の生成
	 */
	createDateCalendarHeader () {
		return html`
			<div class="header">
				<!-- 前月のカレンダー表示ボタン -->
				${this.fix ? html`` : html`
					<bui-icon class="icon icon-button hl" icon="fas fa-angle-left" tabindex="0" @click=${this.lastMonth}></bui-icon>
				`}

				<!-- 年月の表示 -->
				<span @click=${this.selectMonthCalendar}>
					${this.showDate.getFullYear()}年 ${this.showDate.getMonth() + 1}月
				</span>

				<!-- 翌年のカレンダー表示ボタン -->
				${this.fix ? html`` : html`
					<bui-icon class="icon icon-button hl" icon="fas fa-angle-right" tabindex="0" @click=${this.nextMonth}></bui-icon>
				`}
			</div>
		`
	}

	/**
	 * 日カレンダーフッター部の生成
	 */
	createDateCalendarFooter () {
		const currDate = new Date()
		const hideTodayBtn = this.fix && (this.currDate.getFullYear() !== currDate.getFullYear() || this.currDate.getMonth() !== currDate.getMonth())
		return html`
		<div class="footer">
			${hideTodayBtn ? html`` : html`
				<button class=${this.buttonClass} @click="${this.setToday}">TODAY</button>
			`}
		</div>
		`
	}

	/**
	 * カレンダーフッター部ボタンのクラス属性
	 */
	get buttonClass () {
		return classMap({
			"bui-button": true,
			"is-text": true,
			"is-primary": !this.secondary,
			"is-secondary": this.secondary
		})
	}

	/**
	 * カレンダー曜日セルのクラス属性
	 */
	titleCellClass (day) {
		return classMap({
			"title-cell": true,
			sunday: this.emphasisWeekend && day === 0,
			saturday: this.emphasisWeekend && day === 6
		})
	}

	/**
	 * 前月カレンダーの表示イベント
	 */
	lastMonth () {
		// 日カレンダーボディ部のエレメントを取得
		const dateCalendarBody = this.shadowRoot.querySelector(".dateCalendarBody")
		// スライドアニメーションのクラスを一定時間付与する
		dateCalendarBody.classList.add("slide-right-out")
		setTimeout(() => {
			dateCalendarBody.classList.remove("slide-right-out")
			dateCalendarBody.classList.add("slide-left-in")
			setTimeout(() => {
				dateCalendarBody.classList.remove("slide-left-in")
			}, 200)

			// 先月の最終日よりも今月の最終日のほうが数値として大きければ、日付は先月の最終日にする。
			plusMonth(this.showDate, -1)
			this.requestUpdate()
		}, 200)
	}

	/**
	 * 翌月カレンダーの表示イベント
	 */
	nextMonth () {
		// 日カレンダーボディ部のエレメントを取得
		const dateCalendarBody = this.shadowRoot.querySelector(".dateCalendarBody")
		// スライドアニメーションのクラスを一定時間付与する
		dateCalendarBody.classList.add("slide-left-out")
		setTimeout(() => {
			dateCalendarBody.classList.remove("slide-left-out")
			dateCalendarBody.classList.add("slide-right-in")
			setTimeout(() => {
				dateCalendarBody.classList.remove("slide-right-in")
			}, 200)

			// 次月の最終日よりも今月の最終日のほうが数値として大きければ、日付は次月の最終日にする。
			plusMonth(this.showDate, 1)
			this.requestUpdate()
		}, 200)
	}

	/**
	 * 現在日付の設定イベント
	 */
	setToday () {
		this.showDate = new Date()
		this.currDate = new Date()
		if (this.mode === Constant.MODE.DateTime) {
			this.page = Constant.MODE.Time
			this.requestUpdate().then(() => {
				// 選択されている時間を中央に配置する
				this.centerCurrTime()
			})
		} else {
			this.settleDate()
		}
	}

	/***************************************************************/
	/* 時間カレンダー */
	/***************************************************************/
	/**
	 * 時間カレンダー生成
	 */
	createTimeCalendar () {
		const hours = [...new Array(24).keys()]
		const minutes = [...new Array(60).keys()]
		return html`
			<div class="time" style=${this.page === Constant.MODE.Time ? "" : "display:none"}>
				<div class="list-area">
					<div class="list hour">
						${hours.map(hour => html`
							<li value=${hour} class=${hour === this.showDate.getHours() ? "selected hl" : "hl"} @click=${this.selectHour}>
								${("0" + hour).slice(-2)}
							</li>
						`)}
					</div>
					<div class="list minute">
						${minutes.map(minute => html`
							<li value=${minute} class=${minute === this.showDate.getMinutes() ? "selected hl" : "hl"} @click=${this.selectMinute}>
							${("0" + minute).slice(-2)}
							</li>
						`)}
					</div>
				</div>
				<div class="footer">
					<button class="bui-button is-text is-error" @click="${this.close}">CANCEL</button>
					<button class=${this.buttonClass} @click="${this.settleDate}">OK</button>
				</div>
			</div>
		`
	}

	/** 時 選択イベント */
	selectHour (e) {
		this.showDate.setHours(e.currentTarget.value)
		this.currDate.setHours(e.currentTarget.value)
		this.requestUpdate()
	}

	/** 分 選択イベント */
	selectMinute (e) {
		this.showDate.setMinutes(e.currentTarget.value)
		this.currDate.setMinutes(e.currentTarget.value)
		this.requestUpdate()
	}
}
